/**
 * Tenant seed data - three retailer COMPANIES flattened from the nested
 * authoring in `company.seed.ts`. Every company's products are always on the
 * global marketplace (no opt-out - deliberate deviation from HLD SM-08), and
 * all three additionally pay for their own branded sites (`ownSiteEnabled`),
 * which `/retailer/:slug` simulates with tenant scope + theming.
 *
 * Note the gap that leaves: the neutral retailer profile rendered for a
 * company WITHOUT an own site is still live code, but no seeded company
 * exercises it. Add a fourth company with `ownSiteEnabled: false` to see it.
 *
 * Stats are DERIVED, never hand-maintained: `storeCount` comes from the
 * nested store roster and `itemCount` / `auctionsEndingToday` from the live
 * listing seed, so every displayed number matches the actual catalog. The
 * "N locations · …" blurb prefix derives from the roster too.
 */
import { ListingFormat, ListingStatus } from '@core/models/listing.enum';
import { TenantState } from '@core/models/tenant.state';

import { seedListings } from '../listing/listing.seed';
import { seedCompanies } from './company.seed';

export const seedTenants = (): TenantState[] => {
  const live = seedListings().filter(listing => listing.status === ListingStatus.Live);
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  return seedCompanies().map(company => {
    const catalog = live.filter(listing => listing.tenantId === company.id);
    return {
      id: company.id,
      companyId: company.companyId,
      slug: company.slug,
      name: company.name,
      shortName: company.shortName,
      subdomain: company.subdomain,
      ownSiteEnabled: company.ownSiteEnabled,
      blurb: `${company.stores.length} locations · ${company.blurbRest}`,
      theme: company.theme,
      policy: company.policy,
      stats: {
        itemCount: catalog.length,
        auctionsEndingToday: catalog.filter(
          listing => listing.format === ListingFormat.Auction && listing.endsAt !== null && listing.endsAt <= endOfToday
        ).length,
        storeCount: company.stores.length
      }
    };
  });
};
